import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  addEdge,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Spinner,
} from '@fluentui/react-components';
import {
  Delete20Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { GetCallFlow, UpdateCallFlow } from '../../../Services/HeedifyServices';
import { getAccessToken } from '../../../Services/GraphServices';
import { useAbility } from '../../../Contexts/AbilityContext';
import NodesPalette from './NodesPalette';
import EditNodePanel from './EditNodePanel';
import EditableActionNode from './EditableActionNode';
import {
  ResourceAccountNodeLabel,
  GreetingNodeLabel,
  HolidaysNodeLabel,
  OpenHoursNodeLabel,
  SurveyNodeLabel,
  RoutingNodeLabel,
  IVRNodeLabel,
  QueueNodeLabel,
  TransferNodeLabel,
  DisconnectNodeLabel,
  PromptNodeLabel,
} from './NodeLabels';
import { generateHierarchicalRoutingNodes } from './HierarchicalRoutingGenerator';
import { createNodesFromOptions } from './RoutingTypes/OptionNodes';
import { createIVREdges } from './RoutingTypes/IVRRouting';
import CustomEdge from './CustomEdge';
import EditableEdge from './EditableEdge';
import EditableDTMFEdge from './EditableDTMFEdge';
import {
  createHandleDeleteNode,
  createHandleEdgeDelete,
  createHandleEdgeLabelChange,
  createHandleEditNode,
  createHandleSaveNode,
  createHandleConnect,
  createHandleDrop,
  createHandleDragOver,
} from './handlers';
import { ConfirmAlert } from '../../GenericComponent/ConfirmAlert';
import './CSS/CallFlowDiagram.css';
/**
 * Composant CallFlowDiagram - Affichage du CallFlow avec React Flow
 * Étape 1 : Affiche uniquement le nœud Resource Account
 */
const CallFlowDiagramInner = ({ st_CallFlows, setCallFlows, ACTIVE_TENANT, Token }) => {
  const { t } = useTranslation();
  const { callFlowId } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const ability = useAbility();
  
  // Types d'edges personnalisés
  const edgeTypes = {
    custom: CustomEdge,
    editable: EditableEdge,
    editableDTMF: EditableDTMFEdge,
  };
  const [loading, setLoading] = useState(true);
  const [callFlowData, setCallFlowData] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapper = useRef(null);
  // Référence pour éviter les boucles infinies
  const previousCallFlowDataRef = useRef(null);
  // État pour le panel d'édition
  const [editingNode, setEditingNode] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [pendingRoutingToOpen, setPendingRoutingToOpen] = useState(null);
  const [pendingActionNode, setPendingActionNode] = useState(null); // Pour nœuds action en attente de config
  // État pour stocker les données des nœuds d'action (Queue, User, Phone)
  const [actionNodesData, setActionNodesData] = useState({});
  
  // 🔐 État pour les permissions calculées
  const [permissions, setPermissions] = useState(null);

  // État pour la confirmation de suppression
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    type: null, // 'node' ou 'edge'
    item: null,
    name: ''
  });

  /**
   * Helper function to save callFlowData to database
   */
  const saveCallFlowToDatabase = useCallback((updatedCallFlowData) => {
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        const scope = updatedCallFlowData.scope || 'default';
        return UpdateCallFlow(
          ACTIVE_TENANT,
          userId,
          updatedCallFlowData.id,
          scope,
          token,
          updatedCallFlowData
        ).then((result) => {
          // Mettre à jour le Redux après sauvegarde
          if (result?.success === true || result?.message === "Updated") {
            if (setCallFlows) {
              const updatedFlows = st_CallFlows?.map(flow => 
                flow.id === updatedCallFlowData.id ? updatedCallFlowData : flow
              ) || [updatedCallFlowData];
              setCallFlows(updatedFlows);
            }
          }
          return result;
        });
      });
  }, [instance, activeAccount, ACTIVE_TENANT, userId, st_CallFlows, setCallFlows]);

  const onNodesDelete = useCallback((nodesToDelete) => { }, []);

  const onEdgesDelete = useCallback((edgesToDelete) => {
  }, []);

  const handleDeleteNodeInternal = useCallback(
    createHandleDeleteNode(setCallFlowData, previousCallFlowDataRef, saveCallFlowToDatabase),
    [setCallFlowData, saveCallFlowToDatabase]
  );

  const handleDeleteNode = useCallback((nodeId) => {
    // Trouver le nom du nœud pour l'afficher dans la confirmation
    let nodeName = nodeId;
    if (nodeId === 'resource-account') nodeName = 'Resource Account';
    else if (nodeId === 'greeting') nodeName = 'Greeting';
    else if (nodeId === 'holidays') nodeName = 'Holidays';
    else if (nodeId === 'open-hours') nodeName = 'Open Hours';
    else if (nodeId.startsWith('routing-')) nodeName = 'Routing';

    setDeleteConfirmation({
      open: true,
      type: 'node',
      item: nodeId,
      name: nodeName
    });
  }, []);

  const handleEdgeDeleteInternal = useCallback(
    createHandleEdgeDelete(setEdges, setCallFlowData, callFlowData, previousCallFlowDataRef, saveCallFlowToDatabase),
    [callFlowData, setEdges, setCallFlowData, saveCallFlowToDatabase]
  );

  const handleEdgeDelete = useCallback((edgeId) => {
    setDeleteConfirmation({
      open: true,
      type: 'edge',
      item: edgeId,
      name: 'cette liaison'
    });
  }, []);

  const handleEdgeLabelChange = useCallback(
    createHandleEdgeLabelChange(setEdges, setNodes, setCallFlowData, callFlowData, previousCallFlowDataRef, saveCallFlowToDatabase),
    [setEdges, setNodes, setCallFlowData, callFlowData, saveCallFlowToDatabase]
  );

  const confirmDelete = useCallback(() => {
    if (deleteConfirmation.type === 'node') {
      handleDeleteNodeInternal(deleteConfirmation.item);
    } else if (deleteConfirmation.type === 'edge') {
      handleEdgeDeleteInternal(deleteConfirmation.item);
    }
    setDeleteConfirmation({ open: false, type: null, item: null, name: '' });
  }, [deleteConfirmation]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation({ open: false, type: null, item: null, name: '' });
  }, []);

  const handleEditNode = useCallback(
    createHandleEditNode(setEditingNode, setIsPanelOpen, callFlowData),
    [callFlowData]
  );

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setEditingNode(null);
  }, []);

  const handleSaveNode = useCallback(
    createHandleSaveNode(saveCallFlowToDatabase, previousCallFlowDataRef, setCallFlowData, handleClosePanel),
    [saveCallFlowToDatabase, handleClosePanel]
  );

  const onConnect = useCallback(
    createHandleConnect(
      nodes,
      setEdges,
      callFlowData,
      setCallFlowData,
      saveCallFlowToDatabase,
      handleEdgeDelete,
      handleEdgeLabelChange,
      previousCallFlowDataRef
    ),
    [nodes, setEdges, callFlowData, setCallFlowData, saveCallFlowToDatabase, handleEdgeDelete, handleEdgeLabelChange]
  );

  // Validation des connexions
  const isValidConnection = useCallback((connection) => {
    // Le Survey ne peut avoir aucune connexion entrante ou sortante
    if (connection.source === 'survey' || connection.target === 'survey') {
      return false;
    }

    // Les nœuds Queue ne peuvent avoir aucune connexion sortante
    if (connection.source && connection.source.startsWith('queue-')) {
      return false;
    }

    // Un routing de type queue ne peut avoir aucune connexion sortante
    if (connection.source && connection.source.startsWith('routing-')) {
      const routingKey = connection.source.replace('routing-', '');
      const routing = callFlowData.routing?.[routingKey];
      if (routing?.type === 'queue') {
        return false;
      }
    }

    return true;
  }, [callFlowData]);

  const onDragOver = useCallback(
    createHandleDragOver(),
    []
  );

  const onDrop = useCallback(
    createHandleDrop(
      reactFlowInstance,
      setNodes,
      callFlowData,
      setCallFlowData,
      setPendingRoutingToOpen,
      handleEditNode,
      handleDeleteNode,
      saveCallFlowToDatabase,
      previousCallFlowDataRef
    ),
    [reactFlowInstance, setNodes, callFlowData, setCallFlowData, setPendingRoutingToOpen, handleEditNode, handleDeleteNode, saveCallFlowToDatabase]
  );

  // Handler pour le changement de label DTMF inline
  const handleDTMFLabelChange = useCallback((edgeId, newLabel, sourceId, targetId, currentLabel) => {

    if (!sourceId || !targetId) {
      return;
    }

    // Extraire le routingKey du source ID
    const routingKey = sourceId.replace('routing-', '');
    const routing = callFlowData?.routing?.[routingKey];

    if (!routing || (routing.type !== 'ivr' && routing.type !== 'IVR')) {
      return;
    }

    if (newLabel === currentLabel || !newLabel) {
      return;
    }


    // Mettre à jour le callFlowData
    const updatedCallFlowData = { ...callFlowData };
    const updatedRouting = { ...updatedCallFlowData.routing[routingKey] };

    if (!updatedRouting.options) {
      updatedRouting.options = {};
    }

    // Trouver l'option actuelle en cherchant par la cible
    let optionToMove = null;

    // Déterminer le type d'action selon le nœud cible
    if (targetId.startsWith('routing-')) {
      const targetRoutingKey = targetId.replace('routing-', '');
      // Chercher l'option qui pointe vers ce routing
      for (const [dtmf, option] of Object.entries(updatedRouting.options)) {
        if (option.type === 'routing' && option.routingName === targetRoutingKey) {
          optionToMove = option;
          delete updatedRouting.options[dtmf];
          break;
        }
      }
      if (!optionToMove) {
        optionToMove = {
          type: 'routing',
          routingName: targetRoutingKey
        };
      }
    } else if (targetId.startsWith('queue-')) {
      const queueId = targetId.replace('queue-', '');
      // Chercher l'option qui pointe vers cette queue
      for (const [dtmf, option] of Object.entries(updatedRouting.options)) {
        if ((option.type === 'queue' || option.type === 'queueAction') && option.queueId === queueId) {
          optionToMove = option;
          delete updatedRouting.options[dtmf];
          break;
        }
      }
      if (!optionToMove) {
        const queueRouting = Object.values(callFlowData.routing || {}).find(
          r => r.type === 'queue' && r.queueId === queueId
        );
        optionToMove = {
          type: 'queue',
          queueId: queueId,
          queueDisplayName: queueRouting?.displayName || 'Queue'
        };
      }
    } else if (targetId.startsWith('transfer-user-')) {
      const userId = targetId.replace('transfer-user-', '');
      // Chercher l'option qui pointe vers cet user
      for (const [dtmf, option] of Object.entries(updatedRouting.options)) {
        if ((option.type === 'transferUser' || option.type === 'transfer') && option.targetId === userId) {
          optionToMove = option;
          delete updatedRouting.options[dtmf];
          break;
        }
      }
      if (!optionToMove) {
        optionToMove = {
          type: 'transferUser',
          targetId: userId,
          targetType: 'user'
        };
      }
    } else if (targetId.startsWith('transfer-pstn-')) {
      const phoneNumber = targetId.replace('transfer-pstn-', '');
      // Chercher l'option qui pointe vers ce phone
      for (const [dtmf, option] of Object.entries(updatedRouting.options)) {
        if ((option.type === 'transferPhone' || option.type === 'transfer') && option.targetId === phoneNumber) {
          optionToMove = option;
          delete updatedRouting.options[dtmf];
          break;
        }
      }
      if (!optionToMove) {
        optionToMove = {
          type: 'transferPhone',
          targetId: phoneNumber,
          targetType: 'phone'
        };
      }
    } else if (targetId === 'disconnect-node') {
      // Chercher l'option disconnect
      for (const [dtmf, option] of Object.entries(updatedRouting.options)) {
        if (option.type === 'disconnect') {
          optionToMove = option;
          delete updatedRouting.options[dtmf];
          break;
        }
      }
      if (!optionToMove) {
        optionToMove = {
          type: 'disconnect'
        };
      }
    }

    // Ajouter l'option avec le nouveau DTMF
    if (optionToMove) {
      updatedRouting.options[newLabel] = optionToMove;
    }

    updatedCallFlowData.routing[routingKey] = updatedRouting;
    // Forcer la régénération du diagramme en ne mettant PAS à jour previousCallFlowDataRef
    // Le useEffect détectera que callFlowData !== previousCallFlowDataRef.current
    setCallFlowData({ ...updatedCallFlowData }); // Créer une nouvelle référence

    // Sauvegarder dans la base de données
    saveCallFlowToDatabase(updatedCallFlowData).then(() => {
    }).catch(err => {
      console.error('Error saving DTMF:', err);
    });
  }, [callFlowData, setCallFlowData, saveCallFlowToDatabase]);

  // Handler pour double-clic sur edge pour éditer le label DTMF
  // Note: L'édition inline est maintenant gérée directement dans EditableDTMFEdge
  const onEdgeDoubleClick = useCallback((event, edge) => {
    // L'édition du DTMF est gérée par l'édition inline dans le composant EditableDTMFEdge
    // Pas besoin de prompt ici
    return;
  }, []);

  /**
   * Ouvrir le panel quand le routing est créé dans callFlowData
   */
  useEffect(() => {
    if (pendingRoutingToOpen) {
      const routingKey = pendingRoutingToOpen.replace('routing-', '');
      if (callFlowData?.routing?.[routingKey]) {
        handleEditNode(pendingRoutingToOpen);
        setPendingRoutingToOpen(null);
      }
    }
  }, [callFlowData, pendingRoutingToOpen, handleEditNode]);

  /**
   * Chargement des données du CallFlow depuis l'API (toujours à jour)
   */
  useEffect(() => {
    const loadCallFlow = async () => {
      setLoading(true);
      
      // ✅ Toujours charger depuis le serveur pour avoir les données à jour
      // Trouver le scope depuis le cache (juste pour le scope)
      const existingFlow = st_CallFlows?.find((f) => f.id === callFlowId);
      const flowScope = existingFlow?.scope || 'default';
      
      try {
        const token = Token || (await getAccessToken(instance, activeAccount));
        const response = await GetCallFlow(ACTIVE_TENANT, userId, callFlowId, flowScope, token);
        console.log("Call flow data loaded from server: chart", response);
        if (response) {
          setCallFlowData(response);
        } else {
          console.warn('⚠️ No data returned for callflow:', callFlowId);
        }
      } catch (error) {
        console.error('❌ Error loading callflow:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (callFlowId && callFlowId !== 'newCallFlow') {
      loadCallFlow();
    } else {
      setLoading(false);
    }
  }, [callFlowId, st_CallFlows, ACTIVE_TENANT, Token, instance, activeAccount, userId]);
  
  /**
   * 🔐 Calculer les permissions basées sur le scope du callflow
   */
  useEffect(() => {
    if (!callFlowData?.scope || !ability) {
      return;
    }

    const flowScope = callFlowData.scope;
    const calculatedPermissions = {
      resourceAccount: {
        canCreate: ability?.can("create", `Flow.resourceAccount@${flowScope}`),
        canRead: ability?.can("read", `Flow.resourceAccount@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.resourceAccount@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.resourceAccount@${flowScope}`),
      },
      greeting: {
        canCreate: ability?.can("create", `Flow.greetingState@${flowScope}`),
        canRead: ability?.can("read", `Flow.greetingState@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.greetingState@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.greetingState@${flowScope}`),
        messages: {
          canCreate: ability?.can("create", `Flow.messages@${flowScope}`),
          canRead: ability?.can("read", `Flow.messages@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.messages@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.messages@${flowScope}`),
        }
      },
      holidays: {
        canCreate: ability?.can("create", `Flow.holiday@${flowScope}`),
        canRead: ability?.can("read", `Flow.holiday@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.holiday@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.holiday@${flowScope}`),
        openHour: {
          canCreate: ability?.can("create", `Flow.OpenHour@${flowScope}`),
          canRead: ability?.can("read", `Flow.OpenHour@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.OpenHour@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.OpenHour@${flowScope}`),
        },
        holidayActions: {
          canCreate: ability?.can("create", `Flow.holidayActions@${flowScope}`),
          canRead: ability?.can("read", `Flow.holidayActions@${flowScope}`),
          canUpdate: ability?.can("update", `Flow.holidayActions@${flowScope}`),
          canDelete: ability?.can("delete", `Flow.holidayActions@${flowScope}`),
        }
      },
      routing: {
        canCreate: ability?.can("create", `Flow.routing@${flowScope}`),
        canRead: ability?.can("read", `Flow.routing@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.routing@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.routing@${flowScope}`),
      },
      recording: {
        canCreate: ability?.can("create", `Flow.recording@${flowScope}`),
        canRead: ability?.can("read", `Flow.recording@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.recording@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.recording@${flowScope}`),
      },
      survey: {
        canCreate: ability?.can("create", `Flow.survey@${flowScope}`),
        canRead: ability?.can("read", `Flow.survey@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.survey@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.survey@${flowScope}`),
      },
      variables: {
        canCreate: ability?.can("create", `Flow.variables@${flowScope}`),
        canRead: ability?.can("read", `Flow.variables@${flowScope}`),
        canUpdate: ability?.can("update", `Flow.variables@${flowScope}`),
        canDelete: ability?.can("delete", `Flow.variables@${flowScope}`),
      }
    };

    setPermissions(calculatedPermissions);
  }, [callFlowData?.scope, ability]);
  
  /**
   * Génération des nœuds à partir des données CallFlow
   * Régénère les nœuds quand callFlowData change
   */
  useEffect(() => {
    if (!callFlowData) {
      return;
    }


    // Créer des handlers inline pour éviter les dépendances cycliques
    const inlineHandleEditNode = (nodeId) => {
      setEditingNode(nodeId);
      setIsPanelOpen(true);
    };

    const inlineHandleDeleteNode = (nodeId) => {
      setDeleteConfirmation({
        open: true,
        type: 'node',
        item: nodeId,
        name: nodeId
      });
    };

    const inlineHandleEdgeDelete = (edgeId) => {
      setDeleteConfirmation({
        open: true,
        type: 'edge',
        item: edgeId,
        name: 'cette liaison'
      });
    };

    const inlineHandleEdgeLabelChange = (edgeId, newLabel) => {
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === edgeId
            ? { ...edge, label: newLabel, data: { ...edge.data, label: newLabel } }
            : edge
        )
      );
    };

    const generatedNodes = [];
    const generatedEdgesLocal = [];
    let edgeId = 1;
    // Espacement pour garantir une longueur de liaison fixe
    const NODE_X_POSITION = 200;
    const EDGE_LENGTH = 80; // Longueur fixe de la liaison visible
    let currentYPosition = 50;
    let lastNodeHeight = 0;
    // Fonction pour calculer la position Y en tenant compte de la hauteur du nœud précédent
    const getNodeYPosition = () => {
      const position = currentYPosition + lastNodeHeight + EDGE_LENGTH;
      return position;
    };
    // 1. Nœud Resource Account (toujours affiché)
    const resourceAccountNode = {
      id: 'resource-account',
      type: 'default',
      position: { x: NODE_X_POSITION + 65, y: currentYPosition }, // +65 pour centrer le cercle avec les rectangles
      deletable: true,
      data: {
        label: (
          <>
            <Handle
              type="source"
              position={Position.Bottom}
              id="source-bottom"
              style={{ background: '#555' }}
            />
            <ResourceAccountNodeLabel
              callFlowData={callFlowData}
              handleEditNode={handleEditNode}
              handleDeleteNode={handleDeleteNode}
            />
          </>
        ),
      },
      style: {
        background: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '50%',
        padding: '15px',
        width: '180px',
        height: '180px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    };
    generatedNodes.push(resourceAccountNode);
    lastNodeHeight = 180; // Hauteur du nœud Resource Account (réelle hauteur du cercle)
    currentYPosition = getNodeYPosition();
    // 2. Nœud Greeting (affiché seulement si au moins un message est activé)
    const greetingMessages = callFlowData.greeting || {};
    const enabledMessages = Object.entries(greetingMessages).filter(
      ([key, msg]) => msg?.enabled === true
    );
    if (enabledMessages.length > 0) {
      const greetingNode = {
        id: 'greeting',
        type: 'default',
        position: { x: NODE_X_POSITION, y: currentYPosition },
        deletable: true,
        data: {
          label: (
            <>
              <Handle
                type="target"
                position={Position.Top}
                id="target-top"
                style={{ background: '#555' }}
              />
              <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ background: '#555' }}
              />
              <GreetingNodeLabel
                callFlowData={callFlowData}
                handleEditNode={handleEditNode}
                handleDeleteNode={handleDeleteNode}
              />
            </>
          ),
        },
        style: {
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #10b981',
          borderRadius: '10px',
          padding: '15px',
          minWidth: '250px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      };
      generatedNodes.push(greetingNode);
      lastNodeHeight = 150 + (enabledMessages.length * 25); // Hauteur dynamique selon le nombre de messages
      currentYPosition = getNodeYPosition();
      // Créer l'edge entre Resource Account et Greeting
      const edgeIdStr = `edge-${edgeId++}`;
      generatedEdgesLocal.push({
        id: edgeIdStr,
        source: 'resource-account',
        target: 'greeting',
        type: 'custom',
        animated: true,
        style: { stroke: 'rgb(98, 100, 167)', strokeWidth: 1.5 },
        deletable: true,
        markerEnd: {
          type: 'arrowclosed',
          color: 'rgb(98, 100, 167)',
        },
        data: {
          onDelete: (id) => {
            setEdges((eds) => eds.filter((e) => e.id !== id));
          },
        },
      });
    }
    // 3. Nœud Holidays (affiché si des weekends ou holidays sont configurés ET si l'utilisateur a les permissions)
    const holidaysData = callFlowData.deskOpen?.holidays || {};
    const hasWeekendDays = holidaysData.weekendDays && holidaysData.weekendDays.length > 0;
    const hasHolidayDays = holidaysData.daysOff && holidaysData.daysOff.length > 0;
    const canReadHolidays = permissions?.holidays?.canRead;
    if ((hasWeekendDays || hasHolidayDays) && canReadHolidays) {
      const holidaysNode = {
        id: 'holidays',
        type: 'default',
        position: { x: NODE_X_POSITION, y: currentYPosition },
        deletable: true,
        data: {
          label: (
            <>
              <Handle
                type="target"
                position={Position.Top}
                id="target-top"
                style={{ background: '#555' }}
              />
              <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ background: '#555' }}
              />
              <HolidaysNodeLabel
                callFlowData={callFlowData}
                handleEditNode={handleEditNode}
                handleDeleteNode={handleDeleteNode}
              />
            </>
          ),
        },
        style: {
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #f97316',
          borderRadius: '10px',
          padding: '15px',
          minWidth: '250px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      };
      generatedNodes.push(holidaysNode);
      lastNodeHeight = 130; // Hauteur estimée du nœud Holidays
      currentYPosition = getNodeYPosition();
      // Créer l'edge entre le dernier nœud et Holidays
      const sourceNodeId = enabledMessages.length > 0 ? 'greeting' : 'resource-account';
      const holidayEdgeId = `edge-${edgeId++}`;
      generatedEdgesLocal.push({
        id: holidayEdgeId,
        source: sourceNodeId,
        target: 'holidays',
        type: 'custom',
        animated: true,
        style: { stroke: 'rgb(98, 100, 167)', strokeWidth: 1.5 },
        deletable: true,
        markerEnd: {
          type: 'arrowclosed',
          color: 'rgb(98, 100, 167)',
        },
        data: {
          onDelete: (id) => {
            setEdges((eds) => eds.filter((e) => e.id !== id));
          },
        },
      });
    }
    // 4. Nœud Open Hours (affiché si des heures d'ouverture sont configurées ET si l'utilisateur a les permissions sur holidays)
    const openHoursData = callFlowData.deskOpen?.openHours || {};
    const hasOpenHours = Object.keys(openHoursData).some(key => {
      const dayRanges = openHoursData[key];
      return Array.isArray(dayRanges) && dayRanges.length > 0;
    });
    // Open Hours nécessite la permission de lecture sur holidays (car c'est une sous-partie de holidays)
    if (hasOpenHours && canReadHolidays) {
      // Compter le nombre de jours avec des heures configurées
      const daysWithHours = Object.keys(openHoursData).filter(key => {
        const dayRanges = openHoursData[key];
        return Array.isArray(dayRanges) && dayRanges.length > 0;
      }).length;
      const openHoursNode = {
        id: 'open-hours',
        type: 'default',
        position: { x: NODE_X_POSITION, y: currentYPosition },
        deletable: true,
        data: {
          label: (
            <>
              <Handle
                type="target"
                position={Position.Top}
                id="target-top"
                style={{ background: '#555' }}
              />
              <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ background: '#555' }}
              />
              <OpenHoursNodeLabel
                callFlowData={callFlowData}
                handleEditNode={handleEditNode}
                handleDeleteNode={handleDeleteNode}
              />
            </>
          ),
        },
        style: {
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #3b82f6',
          borderRadius: '10px',
          padding: '15px',
          minWidth: '250px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      };
      generatedNodes.push(openHoursNode);
      lastNodeHeight = 120; // Hauteur estimée du nœud Open Hours
      currentYPosition = getNodeYPosition();
      // Créer l'edge entre le dernier nœud et Open Hours
      let sourceNodeId = 'resource-account';
      // Vérifier les permissions : si holidays n'est pas visible, ne pas l'utiliser comme source
      if ((hasWeekendDays || hasHolidayDays) && canReadHolidays) {
        sourceNodeId = 'holidays';
      } else if (enabledMessages.length > 0) {
        sourceNodeId = 'greeting';
      }
      const openHoursEdgeId = `edge-${edgeId++}`;
      generatedEdgesLocal.push({
        id: openHoursEdgeId,
        source: sourceNodeId,
        target: 'open-hours',
        type: 'custom',
        animated: true,
        style: { stroke: 'rgb(98, 100, 167)', strokeWidth: 1.5 },
        deletable: true,
        markerEnd: {
          type: 'arrowclosed',
          color: 'rgb(98, 100, 167)',
        },
        data: {
          onDelete: (id) => {
            setEdges((eds) => eds.filter((e) => e.id !== id));
          },
        },
      });
    }

    // Préparer les données de routing pour utilisation ultérieure
    const routingData = callFlowData.routing || {};
    const routing0 = routingData["0"];

    // 5. Nœud Routing - Affichage hiérarchique
    let routingResult = null;

    if (routing0 && routing0.enabled) {
      // Déterminer le source node ID
      let sourceNodeId = 'resource-account';
      // Vérifier les permissions avant de référencer ces nœuds
      // Open Hours et Holidays nécessitent tous deux la permission canReadHolidays
      if (hasOpenHours && canReadHolidays) {
        sourceNodeId = 'open-hours';
      } else if ((hasWeekendDays || hasHolidayDays) && canReadHolidays) {
        sourceNodeId = 'holidays';
      } else if (enabledMessages.length > 0) {
        sourceNodeId = 'greeting';
      }

      // Utiliser le générateur hiérarchique
      routingResult = generateHierarchicalRoutingNodes(
        callFlowData,
        sourceNodeId,
        currentYPosition,
        {
          handleEditNode,
          handleDeleteNode,
          onEdgeDelete: handleEdgeDelete,
          onEdgeLabelChange: handleEdgeLabelChange,
          onDTMFLabelChange: handleDTMFLabelChange,
        },
        edgeId
      );

      // Ajouter les nœuds et edges générés
      generatedNodes.push(...routingResult.nodes);
      generatedEdgesLocal.push(...routingResult.edges);

      // Mettre à jour edgeId
      edgeId = routingResult.nextEdgeId;
    }

    // 6. Nœud Survey - Affiché seul en haut à gauche, sans liaisons
    if (callFlowData.survey?.enabled) {
      // Position du nœud survey à gauche
      const surveyXPosition = NODE_X_POSITION - 300; // Réduit l'écart pour rapprocher Survey du Resource Account
      const surveyYPosition = 50; // En haut, même hauteur que le Resource Account

      const surveyNode = {
        id: 'survey',
        type: 'default',
        position: { x: surveyXPosition, y: surveyYPosition },
        deletable: true,
        draggable: true,
        data: {
          label: (
            <SurveyNodeLabel
              callFlowData={callFlowData}
              handleEditNode={handleEditNode}
              handleDeleteNode={handleDeleteNode}
            />
          ),
        },
        style: {
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #f59e0b',
          borderRadius: '10px',
          padding: '15px',
          minWidth: '250px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      };

      generatedNodes.push(surveyNode);
      // Pas d'edges pour le Survey - c'est un nœud isolé
    }

    setNodes(generatedNodes);
    setEdges(generatedEdgesLocal);

  }, [callFlowData, permissions, setNodes, setEdges]);

  /**
   * Retour à la liste des CallFlows
   */
  const handleBack = () => {
    navigate('/callFlows');
  };

  if (loading) {
    return (
      <div className="diagram-loading">
        <div> <Spinner /></div>
      </div>
    );
  }

  if (!callFlowData) {
    return (
      <div className="diagram-error">
        <div>❌ {t('CALL_FLOW_NOT_FOUND')}</div>
        <Button onClick={handleBack}>{t('BACK')}</Button>
      </div>
    );
  }

  return (
    <div className="callflow-diagram-container">
      {/* Panel gauche - Liste des nœuds disponibles */}
      <NodesPalette callFlowData={callFlowData} />
      {/* Zone React Flow */}
      <div className="diagram-flow-area" ref={reactFlowWrapper}>
        {/* Nom du CallFlow en haut à gauche - position fixe */}
        <div className="diagram-callflow-name">
          {callFlowData?.displayName || 'Call Flow'}
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          onNodeClick={(event, node) => {
            handleEditNode(node.id);
          }}
          onEdgeDoubleClick={onEdgeDoubleClick}
          onInit={setReactFlowInstance}
          onDragOver={onDragOver}
          onDrop={onDrop}
          edgeTypes={edgeTypes}
          deleteKeyCode="Delete"
          defaultEdgeOptions={{
            deletable: true,
          }}
          zoomOnDoubleClick={false}
          fitView
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Controls className="diagram-controls" />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

        </ReactFlow>
      </div>
      {/* Panel d'édition à droite */}
      <EditNodePanel
        node={editingNode}
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        onSave={handleSaveNode}
        callFlowData={callFlowData}
        setCallFlowData={setCallFlowData}
        ACTIVE_TENANT={ACTIVE_TENANT}
        actionNodesData={actionNodesData}
        setActionNodesData={setActionNodesData}
        pendingActionNode={pendingActionNode}
        setPendingActionNode={setPendingActionNode}
        setNodes={setNodes}
        onDeleteNode={handleDeleteNode}
        permissions={permissions}
        isEditMode={true}
      />

      {/* Confirmation de suppression */}
      <ConfirmAlert
        open={deleteConfirmation.open}
        name={deleteConfirmation.name}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};
/**
 * Wrapper avec ReactFlowProvider (requis par React Flow)
 */
const CallFlowDiagramComponent = (props) => {
  return (
    <ReactFlowProvider>
      <CallFlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
};
export default CallFlowDiagramComponent;
